import { Sprite, useTick } from '@pixi/react';
import { getZIndex } from '../../utils/zindex.utils';
import Placeholder from './Placeholder';
import { missingAssetService } from '../../services/MissingAssetService';
import { useRef, useState } from 'react';
import { tileWidth, tileHeight } from '../../data/tileset.config';
import { useGameStore } from '../../store/gameSlice';
import { dungeonService } from '../../services/DungeonService';
import { ITEMS } from '../../data/inventory.config';
import { GroundItem, FlyingItem } from '../../types/inventory.types';
import { useSpritesStore } from '../../store';
import { resolveIconTexture } from '../../utils/iconTexture.utils';
import { netService } from '../../services/NetService';
import { PickupNotificationDisplay } from './PickupNotificationDisplay';

/** Size of a ground-item icon relative to one tile. */
const ICON_SCALE_RATIO = 0.6;

/** Co-op only: shared (common) ground items render smaller so a player can tell
 *  them apart from their own (possessed) loot at a glance. No effect in solo. */
const SHARED_ITEM_SCALE = 0.8;

/** Progress threshold after which the item starts shrinking. */
const SHRINK_START = 0.6;

export const GroundItemsRenderer = () => {
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const [, setRenderTick] = useState(0);
  const prevCountRef = useRef(0);
  const allTextures = useSpritesStore((s) => s.textures);

  useTick((deltaTime) => {
    dungeonService.inventoryService.updateFlyingItems(deltaTime);

    const groundCount =
      dungeonService.inventoryService.getAllGroundItems().length;
    const flyingCount = dungeonService.inventoryService.getFlyingItems().length;
    const notifCount =
      dungeonService.inventoryService.getPickupNotifications().length;
    const total = groundCount + flyingCount + notifCount;

    if (total !== prevCountRef.current || flyingCount > 0 || notifCount > 0) {
      prevCountRef.current = total;
      setRenderTick((p) => p + 1);
    }
  });

  const groundItems: GroundItem[] =
    dungeonService.inventoryService.getAllGroundItems();
  const flyingItems: FlyingItem[] =
    dungeonService.inventoryService.getFlyingItems();
  const notifications =
    dungeonService.inventoryService.getPickupNotifications();
  const iconSize = tileWidth * ICON_SCALE_RATIO * zoomFactor;

  // Group items by tile to offset overlapping stacks
  const tileGroups = new Map<string, GroundItem[]>();
  for (const item of groundItems) {
    const key = `${item.x},${item.y}`;
    const existing = tileGroups.get(key);
    if (existing) {
      existing.push(item);
    } else {
      tileGroups.set(key, [item]);
    }
  }

  const OVERLAP_OFFSET = iconSize * 0.3;

  // Flat output: items + flying items + notifications are direct children of
  // the camera container so each one depth-sorts by its own Y position.
  return (
    <>
      {groundItems.map((item) => {
        const key = `${item.x},${item.y}`;
        const group = tileGroups.get(key) ?? [item];
        const idx = group.indexOf(item);
        const count = group.length;
        // Spread items radially when stacked; single item stays centered
        const angle = count > 1 ? (idx / count) * Math.PI * 2 : 0;
        const offsetX = count > 1 ? Math.cos(angle) * OVERLAP_OFFSET : 0;
        const offsetY = count > 1 ? Math.sin(angle) * OVERLAP_OFFSET : 0;
        const spriteKey = ITEMS[item.itemId]?.sprite;
        const tex = resolveIconTexture(allTextures, spriteKey);
        const x = item.x * tileWidth * zoomFactor + offsetX;
        const y = -item.y * tileHeight * zoomFactor + offsetY;
        const z = getZIndex({ type: 'groundItem', posY: item.y });
        const size =
          item.mode === 'common' && netService.session !== null
            ? iconSize * SHARED_ITEM_SCALE
            : iconSize;
        if (!tex) {
          missingAssetService.report(spriteKey, 'item');
          return (
            <Placeholder
              key={item.id}
              x={x}
              y={y}
              width={size}
              height={size}
              anchor={[0.5, 0.5]}
              zIndex={z}
            />
          );
        }
        return (
          <Sprite
            key={item.id}
            texture={tex}
            anchor={0.5}
            x={x}
            y={y}
            width={size}
            height={size}
            zIndex={z}
          />
        );
      })}

      {flyingItems.map((fi) => {
        // Shrink only in the last portion of the animation
        const shrinkT =
          fi.progress > SHRINK_START
            ? (fi.progress - SHRINK_START) / (1 - SHRINK_START)
            : 0;
        const scale = 1 - shrinkT;
        const spriteKey = ITEMS[fi.itemId].sprite;
        const tex = resolveIconTexture(allTextures, spriteKey);
        const x = fi.currentX * zoomFactor;
        const y = -fi.currentY * zoomFactor;
        const z = getZIndex({
          type: 'groundItem',
          posY: fi.currentY / tileHeight,
        });
        if (!tex) {
          missingAssetService.report(spriteKey, 'item');
          return (
            <Placeholder
              key={fi.id}
              x={x}
              y={y}
              width={iconSize * scale}
              height={iconSize * scale}
              anchor={[0.5, 0.5]}
              zIndex={z}
            />
          );
        }
        return (
          <Sprite
            key={fi.id}
            texture={tex as any}
            anchor={0.5}
            x={x}
            y={y}
            width={iconSize * scale}
            height={iconSize * scale}
            zIndex={z}
          />
        );
      })}
      {notifications.map((n) => (
        <PickupNotificationDisplay key={n.id} notification={n} />
      ))}
    </>
  );
};
